import { CURRENT_USER_GOOGLE_INTEGRATION_QUERY } from "@lib/graphql/queries";
import { createContext, type ReactNode, useCallback, useContext, useMemo } from "react";
import { useQuery } from "urql";

interface GoogleIntegration {
  id: string;
  googleEmail: string;
  connectedAt: string;
}

interface CurrentUserGoogleIntegrationContextValue {
  integration: GoogleIntegration | null;
  fetching: boolean;
  refetch: () => void;
}

const CurrentUserGoogleIntegrationContext =
  createContext<CurrentUserGoogleIntegrationContextValue | null>(null);

export function CurrentUserGoogleIntegrationProvider({ children }: { children: ReactNode }) {
  const [{ data, fetching }, reexecute] = useQuery({
    query: CURRENT_USER_GOOGLE_INTEGRATION_QUERY,
  });

  const refetch = useCallback(() => {
    reexecute({ requestPolicy: "network-only" });
  }, [reexecute]);

  const value = useMemo<CurrentUserGoogleIntegrationContextValue>(
    () => ({
      integration: (data?.currentUserGoogleIntegration ?? null) as GoogleIntegration | null,
      fetching,
      refetch,
    }),
    [data, fetching, refetch],
  );

  return (
    <CurrentUserGoogleIntegrationContext.Provider value={value}>
      {children}
    </CurrentUserGoogleIntegrationContext.Provider>
  );
}

export function useCurrentUserGoogleIntegration(): CurrentUserGoogleIntegrationContextValue {
  const ctx = useContext(CurrentUserGoogleIntegrationContext);
  if (!ctx) {
    throw new Error(
      "useCurrentUserGoogleIntegration must be used inside <CurrentUserGoogleIntegrationProvider>",
    );
  }
  return ctx;
}
